import { useRef, useEffect, useCallback } from "react";
import { useMotionValueEvent } from "framer-motion";

/**
 * Frame-by-frame canvas scrubbing tied to a framer-motion scroll progress value.
 * Preloads `totalFrames` images and draws the frame matching the scroll position.
 * Returns the ref to attach to the <canvas> element.
 */
export function useScrollFrames(scrollYProgress, totalFrames, framePath, options = {}) {
  const { fitMode = "cover", scale = 1, verticalAnchor = 0.5 } = options;
  const canvasRef = useRef(null);
  const framesRef = useRef([]);

  const drawFrame = useCallback((idx) => {
    const canvas = canvasRef.current;
    const img = framesRef.current[idx];
    if (!canvas || !img || !img.complete || img.naturalWidth === 0) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr)) {
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // Paint the canvas with the same matte-black background the animation uses,
    // so any letterboxed/empty area blends seamlessly with the image content.
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, w, h);
    const ir = img.naturalWidth / img.naturalHeight;
    const cr = w / h;
    let dw, dh;
    if (fitMode === "contain") {
      if (cr > ir) { dh = h; dw = h * ir; } else { dw = w; dh = w / ir; }
    } else {
      // cover — fill, crop overflow
      if (cr > ir) { dw = w; dh = w / ir; } else { dh = h; dw = h * ir; }
    }
    dw *= scale;
    dh *= scale;
    const dx = (w - dw) / 2;
    const dy = (h - dh) * verticalAnchor;
    ctx.clearRect(0, 0, w, h);
    // Re-paint matte-black after clear (clearRect makes it transparent)
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, dx, dy, dw, dh);
  }, [fitMode, scale, verticalAnchor]);

  const drawAtProgress = useCallback(
    (p) => {
      const idx = Math.max(0, Math.min(totalFrames - 1, Math.floor(p * (totalFrames - 1))));
      drawFrame(idx);
    },
    [drawFrame, totalFrames]
  );

  // Preload frames
  useEffect(() => {
    const images = [];
    let loaded = 0;
    for (let i = 1; i <= totalFrames; i++) {
      const img = new Image();
      img.src = framePath(i);
      img.onload = () => {
        loaded += 1;
        if (loaded === 6) drawFrame(0);
      };
      images.push(img);
    }
    framesRef.current = images;
  }, [drawFrame, totalFrames, framePath]);

  // Redraw on resize
  useEffect(() => {
    const onResize = () => drawAtProgress(scrollYProgress.get());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [scrollYProgress, drawAtProgress]);

  // Redraw on scroll
  useMotionValueEvent(scrollYProgress, "change", drawAtProgress);

  return canvasRef;
}
